import { action, computed, makeObservable, observable } from "mobx";

import { ParameterValue, isInputParameterNumeric } from "../utils";

import { ComponentType, Option, Parameter } from "@/queries/models/types";

type ParameterStoreData = Partial<Parameter> & {
  parameters?: Parameter[];
};

export class ParameterStore {
  @observable private _parameters?: ParameterStore[];
  @observable private _value?: ParameterValue;
  @observable private _isCustom?: boolean;
  @observable private _componentType?: ComponentType;
  @observable private _placeholder?: string;
  @observable private _options?: Option[];
  @observable private _name?: string;
  @observable private _paramId?: string;
  @observable private _isDisabled?: boolean;
  @observable private _default?: ParameterValue;
  @observable private _min?: ParameterValue;
  @observable private _max?: ParameterValue;
  @observable private _step?: ParameterValue;
  @observable private _isInt?: boolean;

  constructor(parameter: ParameterStoreData) {
    this._parameters = parameter.parameters?.map(
      param =>
        new ParameterStore({
          ...param,
          value: parameter?.value?.[param.param_id],
        }),
    );
    this._value = parameter.value;
    this._isCustom = parameter.is_custom;
    this._componentType = parameter.component_type;
    this._placeholder = parameter.placeholder;
    this._options = parameter.options;
    this._name = parameter.name;
    this._paramId = parameter.param_id;
    this._isDisabled = parameter.is_disabled;
    this._default = parameter.default;

    if (parameter.component_type === ComponentType.SLIDER) {
      this._step = parameter.step;
    }

    if (parameter.component_type === ComponentType.INPUT) {
      this._isInt = parameter.is_int;
    }

    if (
      parameter.component_type === ComponentType.SLIDER ||
      parameter.component_type === ComponentType.INPUT
    ) {
      this._min = parameter.min;
      this._max = parameter.max;
    }

    this.normalizeInputParameterBounds();

    makeObservable(this);
  }

  @action
  private normalizeInputParameterBounds() {
    if (this._componentType !== ComponentType.INPUT) {
      return;
    }

    if (!isInputParameterNumeric(this._default, this._min, this._max)) {
      this._min = undefined;
      this._max = undefined;
      this._isInt = false;
    }
  }

  @action
  updateValue(value: ParameterValue) {
    this._value = value;
  }

  @action
  updateIsEnabled(isEnabled: boolean) {
    this._isDisabled = !isEnabled;
  }

  @action
  updateKey(key: string) {
    this._paramId = key;
    this._name = key;
  }

  @action
  updateParamId(paramId: string) {
    this._paramId = paramId;
  }

  @action
  updateName(name: string) {
    this._name = name;
  }

  @action
  updateParameter(parameter: Parameter) {
    this._componentType = parameter.component_type;
    this._default = parameter.default;
    this._isCustom = parameter.is_custom;
    this._isDisabled = parameter.is_disabled;
    this._name = parameter.name;
    this._paramId = parameter.param_id;
    this._placeholder = parameter.placeholder;
    this._options = parameter.options;
    this._value = parameter.value;

    if (parameter.component_type === ComponentType.SLIDER) {
      this._step = parameter.step;
    }

    if (parameter.component_type === ComponentType.GROUP) {
      this._parameters = parameter.parameters?.map(
        param =>
          new ParameterStore({
            ...param,
            value: parameter?.value?.[param.param_id],
          }),
      );
    }

    if (
      parameter.component_type === ComponentType.SLIDER ||
      parameter.component_type === ComponentType.INPUT
    ) {
      this._min = parameter.min;
      this._max = parameter.max;
    }

    if (parameter.component_type === ComponentType.INPUT) {
      this._isInt = parameter.is_int;
    }

    this.normalizeInputParameterBounds();
  }

  @action
  updateOptions(options: Option[]) {
    this._options = options;
  }

  @action
  updateDefault(defaultValue: ParameterValue) {
    this._default = defaultValue;

    if (this._componentType === ComponentType.INPUT) {
      this.normalizeInputParameterBounds();
    }
  }

  @action
  updatePlaceholder(placeholder: string) {
    this._placeholder = placeholder;
  }

  @action
  updateMin(min: number) {
    this._min = min;
  }

  @action
  updateMax(max: number) {
    this._max = max;
  }

  @action
  updateStep(step: number) {
    this._step = step;
  }

  @action
  updateIsInt(isInt: boolean) {
    this._isInt = isInt;
  }

  @action
  updateGroupParameters(parameters: Parameter[]) {
    this._parameters = parameters.map(
      param =>
        new ParameterStore({
          ...param,
          value: param?.value?.[param.param_id],
        }),
    );
  }

  @computed
  get value() {
    return this._value;
  }

  @computed
  get step() {
    return this._step;
  }

  @computed
  get isInt() {
    return this._isInt;
  }

  @computed
  get min() {
    if (
      this._componentType === ComponentType.SLIDER ||
      this._componentType === ComponentType.INPUT
    ) {
      return this._min;
    }

    return undefined;
  }

  @computed
  get groupParameters() {
    if (this._componentType === ComponentType.GROUP) {
      return this._parameters;
    }

    return null;
  }

  @computed
  get max() {
    if (
      this._componentType === ComponentType.SLIDER ||
      this._componentType === ComponentType.INPUT
    ) {
      return this._max;
    }

    return undefined;
  }

  @computed
  get placeholder() {
    return this._placeholder;
  }

  @computed
  get options() {
    return this._options;
  }

  @computed
  get parameters() {
    if (this._componentType === ComponentType.GROUP) {
      return this._parameters;
    }

    return [];
  }

  @computed
  get name() {
    return this._name;
  }

  @computed
  get paramId() {
    return this._paramId;
  }

  @computed
  get isDisabled() {
    return !!this._isDisabled;
  }

  @computed
  get isCustom() {
    return !!this._isCustom;
  }

  @computed
  get componentType() {
    return this._componentType;
  }

  @computed
  get isNumericInput() {
    return (
      this._componentType === ComponentType.INPUT &&
      isInputParameterNumeric(this._default, this._min, this._max)
    );
  }

  @computed
  get default() {
    return this._default;
  }

  @computed
  get getParameter(): Parameter {
    const parameterData = {
      component_type: this._componentType,
      default: this._default,
      is_custom: this._isCustom,
      is_disabled: this._isDisabled,
      name: this._name,
      param_id: this._paramId,
      placeholder: this._placeholder,
      options: this._options,
      value: this._value,
    };

    if (this._componentType === ComponentType.GROUP) {
      return {
        ...parameterData,
        component_type: ComponentType.GROUP,
        parameters: this.parameters?.map(p => p.getParameter) || [],
      } as Parameter;
    }

    if (this._componentType === ComponentType.SLIDER) {
      return {
        ...parameterData,
        component_type: ComponentType.SLIDER,
        default: this._default,
        min: this._min,
        max: this._max,
        step: this._step,
        is_int: !!this._isInt,
      } as Parameter;
    }

    if (this._componentType === ComponentType.INPUT) {
      const isNumericInput = isInputParameterNumeric(this._default, this._min, this._max);

      return {
        ...parameterData,
        component_type: ComponentType.INPUT,
        default: this._default,
        is_int: isNumericInput ? !!this._isInt : false,
        ...(isNumericInput ? { min: this._min, max: this._max } : {}),
      } as Parameter;
    }

    return {
      ...parameterData,
      component_type: this._componentType || ComponentType.DROPDOWN,
    } as Parameter;
  }
}
